import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import moderationService, { DashboardData } from '../../services/moderationService';
import aiModelService, { AiModel, CurrentModelResponse } from '../../services/aiModelService';

const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #2D3748;
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #718096;
  font-size: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #2D3748;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.$color || '#4299E1'};
`;

const StatLabel = styled.div`
  color: #718096;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  color: #2D3748;
  font-size: 2rem;
  font-weight: 700;
`;

const StatSubtext = styled.div`
  color: #A0AEC0;
  font-size: 0.75rem;
  margin-top: 0.5rem;
`;

const ChartContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
`;

const ViolationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ViolationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #F7FAFC;
  border-radius: 8px;
`;

const ViolationName = styled.span`
  color: #2D3748;
  font-weight: 500;
`;

const ViolationCount = styled.span`
  color: #4299E1;
  font-weight: 700;
  font-size: 1.125rem;
`;

const ModelSelector = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ModelCard = styled.div<{ $isActive: boolean; $available: boolean }>`
  padding: 1.25rem;
  border-radius: 8px;
  border: 2px solid ${props => props.$isActive ? '#4299E1' : props.$available ? '#E2E8F0' : '#FED7D7'};
  background: ${props => props.$isActive ? '#EBF8FF' : props.$available ? 'white' : '#FFF5F5'};
  cursor: ${props => props.$available ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  opacity: ${props => props.$available ? 1 : 0.6};

  &:hover {
    ${props => props.$available && !props.$isActive && `
      border-color: #4299E1;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(66, 153, 225, 0.15);
    `}
  }
`;

const ModelName = styled.div`
  font-weight: 600;
  color: #2D3748;
  margin-bottom: 0.5rem;
`;

const ModelDescription = styled.div`
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 0.75rem;
`;

const ModelStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #A0AEC0;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #E2E8F0;
`;

const Badge = styled.span<{ $type: 'active' | 'unavailable' }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$type === 'active' ? '#48BB78' : '#FC8181'};
  color: white;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #718096;
`;

const ErrorContainer = styled.div`
  background: #FED7D7;
  color: #C53030;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
`;

export const ModerationDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [models, setModels] = useState<AiModel[]>([]);
  const [currentModel, setCurrentModel] = useState<CurrentModelResponse | null>(null);
  const [switchingModel, setSwitchingModel] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const [data, modelsData, currentModelData] = await Promise.all([
        moderationService.getDashboardData(),
        aiModelService.getAvailableModels(),
        aiModelService.getCurrentModel()
      ]);

      setDashboardData(data);
      setModels(modelsData);
      setCurrentModel(currentModelData);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleModelSwitch = async (modelId: string) => {
    if (!currentModel || modelId === currentModel.currentModel || switchingModel) return;

    const model = models.find(m => m.modelId === modelId);
    if (!model?.available) {
      toast.error('This model is not available');
      return;
    }

    try {
      setSwitchingModel(true);
      const result = await aiModelService.setCurrentModel(modelId);
      toast.success(result.message);

      // Reload current model data
      const updatedModel = await aiModelService.getCurrentModel();
      setCurrentModel(updatedModel);
    } catch (err: any) {
      console.error('Failed to switch model:', err);
      toast.error(err.response?.data?.error || 'Failed to switch model');
    } finally {
      setSwitchingModel(false);
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>Loading dashboard data...</LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error || !dashboardData) {
    return (
      <DashboardContainer>
        <ErrorContainer>{error || 'No data available'}</ErrorContainer>
      </DashboardContainer>
    );
  }

  const { overall, ai, violations, reviewTime } = dashboardData;

  return (
    <DashboardContainer>
      <PageHeader>
        <Title>📊 Moderation Dashboard</Title>
        <Subtitle>Overview of content moderation statistics and AI performance</Subtitle>
      </PageHeader>

      {/* Overall Stats */}
      <Section>
        <SectionTitle>📈 Overall Statistics</SectionTitle>
        <StatsGrid>
          <StatCard $color="#4299E1">
            <StatLabel>Total Stories</StatLabel>
            <StatValue>{overall.totalStories}</StatValue>
          </StatCard>

          <StatCard $color="#48BB78">
            <StatLabel>Approved</StatLabel>
            <StatValue>{overall.approved}</StatValue>
            <StatSubtext>Approval Rate: {overall.approvalRate}%</StatSubtext>
          </StatCard>

          <StatCard $color="#F56565">
            <StatLabel>Rejected</StatLabel>
            <StatValue>{overall.rejected}</StatValue>
          </StatCard>

          <StatCard $color="#ED8936">
            <StatLabel>Pending Review</StatLabel>
            <StatValue>{overall.adminReviewPending}</StatValue>
          </StatCard>

          <StatCard $color="#9F7AEA">
            <StatLabel>Revision Requested</StatLabel>
            <StatValue>{overall.revisionRequested}</StatValue>
          </StatCard>

          <StatCard $color="#805AD5">
            <StatLabel>Appeals Pending</StatLabel>
            <StatValue>{overall.appealPending}</StatValue>
            <StatSubtext>Success Rate: {overall.appealSuccessRate}%</StatSubtext>
          </StatCard>
        </StatsGrid>
      </Section>

      {/* AI Stats */}
      <Section>
        <SectionTitle>🤖 AI Moderation Performance</SectionTitle>
        <StatsGrid>
          <StatCard $color="#38B2AC">
            <StatLabel>Total Analyzed</StatLabel>
            <StatValue>{ai.totalAnalyzed}</StatValue>
          </StatCard>

          <StatCard $color="#48BB78">
            <StatLabel>AI Safe</StatLabel>
            <StatValue>{ai.aiSafe}</StatValue>
          </StatCard>

          <StatCard $color="#F56565">
            <StatLabel>AI Unsafe</StatLabel>
            <StatValue>{ai.aiUnsafe}</StatValue>
          </StatCard>

          <StatCard $color="#4299E1">
            <StatLabel>AI Accuracy</StatLabel>
            <StatValue>{ai.aiAccuracy}%</StatValue>
            <StatSubtext>Avg Confidence: {ai.avgConfidence}%</StatSubtext>
          </StatCard>
        </StatsGrid>
      </Section>

      {/* Violations */}
      {violations.length > 0 && (
        <Section>
          <SectionTitle>⚠️ Violation Types</SectionTitle>
          <ChartContainer>
            <ViolationList>
              {violations.map((violation, idx) => (
                <ViolationItem key={idx}>
                  <ViolationName>{violation.violationType}</ViolationName>
                  <ViolationCount>{violation.count}</ViolationCount>
                </ViolationItem>
              ))}
            </ViolationList>
          </ChartContainer>
        </Section>
      )}

      {/* Review Time */}
      <Section>
        <SectionTitle>⏱️ Review Time Statistics</SectionTitle>
        <StatsGrid>
          <StatCard $color="#667EEA">
            <StatLabel>Average Review Time</StatLabel>
            <StatValue>{reviewTime.avgReviewTimeHours.toFixed(1)}h</StatValue>
          </StatCard>

          <StatCard $color="#48BB78">
            <StatLabel>Fastest Review</StatLabel>
            <StatValue>{reviewTime.minReviewTimeHours.toFixed(1)}h</StatValue>
          </StatCard>

          <StatCard $color="#F56565">
            <StatLabel>Slowest Review</StatLabel>
            <StatValue>{reviewTime.maxReviewTimeHours.toFixed(1)}h</StatValue>
          </StatCard>
        </StatsGrid>
      </Section>

      {/* AI Model Selection */}
      <Section>
        <SectionTitle>🎯 AI Model Selection</SectionTitle>
        <ModelSelector>
          <div style={{ marginBottom: '1rem', color: '#718096', fontSize: '0.875rem' }}>
            Current Model: <strong>{currentModel?.config.displayName}</strong>
          </div>
          <ModelGrid>
            {models.map(model => (
              <ModelCard
                key={model.modelId}
                $isActive={model.modelId === currentModel?.currentModel}
                $available={model.available}
                onClick={() => model.available && handleModelSwitch(model.modelId)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <ModelName>{model.displayName}</ModelName>
                  {model.modelId === currentModel?.currentModel ? (
                    <Badge $type="active">ACTIVE</Badge>
                  ) : !model.available ? (
                    <Badge $type="unavailable">N/A</Badge>
                  ) : null}
                </div>
                <ModelDescription>{model.description}</ModelDescription>
                <ModelStats>
                  <span>⚡ {model.performance.avgResponseTime}s</span>
                  <span>🎯 {model.performance.accuracy}%</span>
                  <span>📊 {model.performance.totalAnalyses} tests</span>
                </ModelStats>
              </ModelCard>
            ))}
          </ModelGrid>
        </ModelSelector>
      </Section>
    </DashboardContainer>
  );
};

export default ModerationDashboard;
